import { Component, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api.service';
import { BasePage } from '../../../base-page';
import { MessageService } from 'src/app/services/message.service';
import { IIKOReplaceProductModel } from 'src/app/models/store/replace-product/iiko-replace-product.model';
import { IIKONomenklatureModel } from 'src/app/models/store/replace-product/iiko-nomenklature.model';

interface ReplacementProductFormGroup {
  product: FormControl<IIKONomenklatureModel | null>;
}

interface ReplacementFormGroup {
  product: FormControl<IIKONomenklatureModel | null>;
  replacementProducts: FormArray<FormGroup<ReplacementProductFormGroup>>;
}

@Component({
  selector: 'app-store-product-replacement',
  templateUrl: './store-product-replacement.component.html',
  styleUrls: ['./store-product-replacement.component.scss'],
  standalone: false
})
export class StoreProductReplacementComponent extends BasePage implements OnInit, OnChanges {

  @Input() storeId?: string;

  private mApiService = inject(ApiService);
  private mMessageService = inject(MessageService);

  public replacementForm: FormGroup<any>;
  public newRowForm: FormGroup<any>;
  public searchText: string = '';
  public searchedProducts: IIKONomenklatureModel[] = [];
  public newRowSearchedProducts: IIKONomenklatureModel[] = [];
  public newRowReplacementSearchedProducts: IIKONomenklatureModel[] = [];
  public productDropdowns: { [key: number]: any } = {};
  public replacementDropdowns: { [key: string]: any } = {};
  public newRowProductDropdownRef: any;
  public newRowReplacementDropdownRef: any;
  public selectedProductForSearch: { [key: number]: IIKONomenklatureModel | null } = {};
  public filterText: string = '';

  constructor() {
    super();
    this.replacementForm = new FormGroup({
      replacements: new FormArray([])
    });
    this.newRowForm = this.createReplacementGroup();
  }

  ngOnInit(): void {
    if (this.storeId) {
      this.loadReplacements();

      this.addNewRowReplacementProduct();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange && this.storeId) {
      this.loadReplacements();
    }
  }

  get replacements(): FormArray<FormGroup<ReplacementFormGroup>> {
    return this.replacementForm.get('replacements') as FormArray<FormGroup<ReplacementFormGroup>>;
  }

  private createReplacementGroup(replacement?: IIKOReplaceProductModel): FormGroup<ReplacementFormGroup> {
    const product = replacement?.product || null;
    const replacementProducts = replacement?.replacementProducts || [];

    const replacementProductsControls = replacementProducts.map(rp => this.createReplacementProductControl(rp));

    const group = new FormGroup<ReplacementFormGroup>({
      product: new FormControl<IIKONomenklatureModel | null>(product, [Validators.required]),
      replacementProducts: new FormArray(replacementProductsControls) as FormArray<FormGroup<ReplacementProductFormGroup>>
    });

    return group;
  }

  private createReplacementProductControl(product?: IIKONomenklatureModel): FormGroup<ReplacementProductFormGroup> {
    return new FormGroup<ReplacementProductFormGroup>({
      product: new FormControl<IIKONomenklatureModel | null>(product || null)
    });
  }

  public getReplacementProducts(index: number): FormArray<FormGroup<ReplacementProductFormGroup>> {
    return this.replacements.at(index).get('replacementProducts') as FormArray<FormGroup<ReplacementProductFormGroup>>;
  }

  public loadReplacements(): void {
    if (!this.storeId) return;

    this.isBusy = true;
    this.mApiService.get<IIKOReplaceProductModel[]>(`/api/admin/store/product/replace/store/${this.storeId}`).subscribe({
      next: (data) => {
        this.replacements.clear();
        if (data && data.length > 0) {
          data.forEach(item => {
            this.replacements.push(this.createReplacementGroup(item));
          });
        }
        this.isBusy = false;
      },
      error: () => {
        this.isBusy = false;
      }
    });
  }

  public addNewRow(): void {
    const product = this.newRowForm.get('product')?.value;
    const replacementProducts = this.getNewRowReplacementProducts();
    const replacementProductsValues = replacementProducts.controls
      .map(control => control.get('product')?.value)
      .filter(x => x);

    const newReplacement: IIKOReplaceProductModel = {
      product: product,
      replacementProducts: replacementProductsValues as IIKONomenklatureModel[]
    };

    this.replacements.push(this.createReplacementGroup(newReplacement));

    // Reset new row form
    this.newRowForm = this.createReplacementGroup();
    (this.newRowForm.controls['replacementProducts'] as FormArray).push(this.createReplacementProductControl());
    
    this.newRowSearchedProducts = [];
    this.newRowReplacementSearchedProducts = [];
    if (this.newRowProductDropdownRef) {
      this.newRowProductDropdownRef.close();
    }
    if (this.newRowReplacementDropdownRef) {
      this.newRowReplacementDropdownRef.close();
    }
  }

  public getNewRowReplacementProducts(): FormArray<FormGroup<ReplacementProductFormGroup>> {
    return this.newRowForm.get('replacementProducts') as FormArray<FormGroup<ReplacementProductFormGroup>>;
  }

  public addNewRowReplacementProduct(): void {
    const replacementProducts = this.getNewRowReplacementProducts();
    replacementProducts.push(this.createReplacementProductControl());
  }

  public removeNewRowReplacementProduct(index: number): void {
    const replacementProducts = this.getNewRowReplacementProducts();
    replacementProducts.removeAt(index);
  }

  public searchNewRowProduct(searchText: string, dropdown: any): void {
    if (!searchText || searchText.length < 2) {
      this.newRowSearchedProducts = [];
      if (dropdown) dropdown.close();
      return;
    }

    this.mApiService.getWithParams<{ search: string }, IIKONomenklatureModel[]>(
      '/api/admin/store/product/replace/search',
      { search: searchText }
    ).subscribe({
      next: (data) => {
        this.newRowSearchedProducts = data;
        if (data.length > 0 && dropdown) {
          dropdown.open();
        } else if (dropdown) {
          dropdown.close();
        }
      }
    });
  }

  public selectNewRowProduct(product: IIKONomenklatureModel): void {
    this.newRowForm.patchValue({ product });
    if (this.newRowProductDropdownRef) {
      this.newRowProductDropdownRef.close();
    }
    this.newRowSearchedProducts = [];
  }

  public searchNewRowReplacementProduct(searchText: string, dropdown: any): void {
    if (!searchText || searchText.length < 2) {
      this.newRowReplacementSearchedProducts = [];
      if (dropdown) dropdown.close();
      return;
    }

    this.mApiService.getWithParams<{ search: string }, IIKONomenklatureModel[]>(
      '/api/admin/store/product/replace/search',
      { search: searchText }
    ).subscribe({
      next: (data) => {
        this.newRowReplacementSearchedProducts = data;
        if (data.length > 0 && dropdown) {
          dropdown.open();
        } else if (dropdown) {
          dropdown.close();
        }
      }
    });
  }

  public selectNewRowReplacementProduct(index: number, product: IIKONomenklatureModel): void {
    const replacementProducts = this.getNewRowReplacementProducts();
    replacementProducts.at(index).patchValue({ product });
    if (this.newRowReplacementDropdownRef) {
      this.newRowReplacementDropdownRef.close();
    }
    this.newRowReplacementSearchedProducts = [];

    this.addNewRowReplacementProduct()
  }

  public openNewRowDropdownIfHasResults(dropdown: any, isReplacement: boolean = false): void {
    const products = isReplacement ? this.newRowReplacementSearchedProducts : this.newRowSearchedProducts;
    if (products.length > 0 && dropdown) {
      dropdown.open();
    }
  }

  public searchProducts(searchText: string, dropdown: any): void {
    if (!searchText || searchText.length < 2) {
      this.searchedProducts = [];
      if (dropdown) dropdown.close();
      return;
    }

    this.mApiService.getWithParams<{ search: string }, IIKONomenklatureModel[]>(
      '/api/admin/store/product/replace/search',
      { search: searchText }
    ).subscribe({
      next: (data) => {
        this.searchedProducts = data;
        if (data.length > 0 && dropdown) {
          dropdown.open();
        } else if (dropdown) {
          dropdown.close();
        }
      }
    });
  }

  public selectProduct(index: number, product: IIKONomenklatureModel): void {
    const replacementGroup = this.replacements.at(index);
    replacementGroup.patchValue({ product });
    this.selectedProductForSearch[index] = product;
    if (this.productDropdowns[index]) {
      this.productDropdowns[index].close();
    }
    this.searchedProducts = [];
  }

  public addReplacementProduct(index: number): void {
    const replacementProducts = this.getReplacementProducts(index);
    replacementProducts.push(this.createReplacementProductControl());
  }

  public removeReplacementProduct(rowIndex: number, replacementIndex: number): void {
    const replacementProducts = this.getReplacementProducts(rowIndex);
    replacementProducts.removeAt(replacementIndex);
  }

  public removeRow(index: number): void {
    this.replacements.removeAt(index);
    delete this.selectedProductForSearch[index];
    delete this.productDropdowns[index];
  }

  public selectReplacementProduct(rowIndex: number, replacementIndex: number, product: IIKONomenklatureModel): void {
    const replacementProducts = this.getReplacementProducts(rowIndex);
    replacementProducts.at(replacementIndex).patchValue({ product });
    const key = `row_${rowIndex}_rep_${replacementIndex}`;
    if (this.replacementDropdowns[key]) {
      this.replacementDropdowns[key].close();
    }
    this.searchedProducts = [];
  }

  public searchReplacementProducts(searchText: string, dropdown: any): void {
    if (!searchText || searchText.length < 2) {
      this.searchedProducts = [];
      if (dropdown) dropdown.close();
      return;
    }

    this.mApiService.getWithParams<{ search: string }, IIKONomenklatureModel[]>(
      '/api/admin/store/product/replace/search',
      { search: searchText }
    ).subscribe({
      next: (data) => {
        this.searchedProducts = data;
        if (data.length > 0 && dropdown) {
          dropdown.open();
        } else if (dropdown) {
          dropdown.close();
        }
      }
    });
  }

  public openDropdownIfHasResults(dropdown: any): void {
    if (this.searchedProducts.length > 0 && dropdown) {
      dropdown.open();
    }
  }

  public save(): void {
    if (!this.storeId) {
      this.mMessageService.addMessage({
        text: 'Необходимо сохранить магазин перед настройкой замены продуктов',
        type: this.mMessageService.types.warning
      });
      return;
    }

    // Filter out empty rows and prepare data
    // Validation: at least one complete row (product + at least one replacement) must exist
    const formValue = this.replacementForm.value;
    const replacements: IIKOReplaceProductModel[] = [];

    formValue.replacements?.forEach((replacement: any) => {
      if (replacement.product) {
        const replacementProducts = replacement.replacementProducts
          .map((rp: any) => rp.product)
          .filter((p: IIKONomenklatureModel) => p !== null);

        if (replacementProducts.length > 0) {
          replacements.push({
            product: replacement.product,
            replacementProducts: replacementProducts
          });
        }
      }
    });

    // Validate that we have at least one complete replacement
    if (replacements.length === 0) {
      this.mMessageService.addMessage({
        text: 'Добавьте хотя бы одну замену продукта (продукт и хотя бы один продукт замены)',
        type: this.mMessageService.types.warning
      });
      return;
    }

    this.isBusy = true;
    this.mApiService.post<IIKOReplaceProductModel[], void>(
      `/api/admin/store/product/replace/store/${this.storeId}`,
      replacements
    ).subscribe({
      next: () => {
        this.isBusy = false;
        this.mMessageService.addMessage({
          text: 'Замена продуктов сохранена',
          type: this.mMessageService.types.success
        });
        this.loadReplacements();
      },
      error: () => {
        this.isBusy = false;
      }
    });
  }

  public isRowEmpty(index: number): boolean {
    const replacement = this.replacements.at(index);
    const product = replacement.get('product')?.value;
    const replacementProducts = this.getReplacementProducts(index);
    return !product && replacementProducts.length === 0;
  }

  public getProductName(product: IIKONomenklatureModel | null): string {
    return product ? product.name : '';
  }

  public isValidTable(): boolean {
    return this.replacements.controls.some((replacement: FormGroup, index: number) => {
      const product = replacement.get('product')?.value;
      const replacementProducts = this.getReplacementProducts(index).controls;
      return product && replacementProducts.length > 1;
    });
  }

  public isRowVisible(index: number): boolean {
    if (!this.filterText || this.filterText.trim() === '') {
      return true;
    }

    const filter = this.filterText.toLowerCase().trim();
    const replacement = this.replacements.at(index);
    const product = replacement.get('product')?.value;

    // Check if main product matches
    if (product && product.name.toLowerCase().includes(filter)) {
      return true;
    }

    // Check if any replacement product matches
    const replacementProducts = this.getReplacementProducts(index);
    for (let i = 0; i < replacementProducts.length; i++) {
      const replacementProduct = replacementProducts.at(i).get('product')?.value;
      if (replacementProduct && replacementProduct.name.toLowerCase().includes(filter)) {
        return true;
      }
    }

    return false;
  }

}
